import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, useNavigate, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import './styles/app-dark.css';
import './styles/mobile.css';
import Spreadsheet from './components/Spreadsheet';
import MobileSpreadsheet from './components/MobileSpreadsheet';
import SpreadsheetManager from './components/SpreadsheetManager';
import NavbarLogin from './components/NavbarLogin';
import EnhancedConnectionsModal from './components/EnhancedConnectionsModal';
import HandCashCallback from './components/HandCashCallback';
import BapsPage from './pages/BapsPage';
import DocsPage from './pages/DocsPage';
import JobExchangePage from './pages/JobExchangePage';
import ThreeDPage from './pages/ThreeDPage';
import TokenPage from './pages/TokenPage';
import ContributionsPage from './pages/ContributionsPage';
import TasksPage from './pages/TasksPage';
import ContractsPage from './pages/ContractsPage';
import EducationalProposalPage from './pages/EducationalProposalPage';
import BitcoinSpreadsheetsPage from './pages/BitcoinSpreadsheetsPage';
import MarketingPage from './pages/MarketingPage';
import SavePage from './pages/SavePage';
import MintPage from './pages/MintPage';
import MarketPage from './pages/MarketPage';
import ExchangePage from './pages/ExchangePage';
import ReactOnChainBugsPage from './pages/ReactOnChainBugsPage';
import ClaudeChat from './components/ClaudeChat';
import SpreadsheetTaskbar from './components/SpreadsheetTaskbar';
import ProofOfConceptBanner from './components/ProofOfConceptBanner';
import DevSidebar from './components/DevSidebar';
import SpreadsheetExchangeView from './components/SpreadsheetExchangeView';
import InstallPrompt from './components/InstallPrompt';
import Footer from './components/Footer';
import DockManager from './components/DockManager';
import { BitcoinService, SpreadsheetData } from './services/BitcoinService';
import { HandCashService, HandCashUser } from './services/HandCashService';
import { useCompactShell, isInWallet } from './mobile/shell';
import { hasCWI, signInWithCWI, getStoredCWIUser } from './mobile/cwi';
import MobileApp from './mobile/MobileApp';

function App() {
  const [bitcoinService, setBitcoinService] = useState<BitcoinService | null>(null);
  const [handcashService] = useState<HandCashService>(new HandCashService());
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState<HandCashUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentSpreadsheet, setCurrentSpreadsheet] = useState<SpreadsheetData | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth > 768);
  const [isClaudeChatOpen, setIsClaudeChatOpen] = useState(false);
  const [showExchange, setShowExchange] = useState(false);
  const [showConnectionsModal, setShowConnectionsModal] = useState(false);
  const [connectedServices, setConnectedServices] = useState<string[]>([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Check localStorage for saved preference, default to true (dark mode)
    const savedMode = localStorage.getItem('darkMode');
    return savedMode !== 'false'; // Default to true unless explicitly set to false
  });
  const hideShellChrome = useCompactShell(); // mobile width or inside bWallet
  const navigate = useNavigate();
  const location = useLocation();

  // Pages that have their own headers and shouldn't show the main app header
  const pagesWithOwnHeaders = [
    '/bitcoin-spreadsheets',
    '/marketing',
    '/educational-proposal',
    '/bap',
    '/developers',
    '/docs',
    '/jobs',
    '/3d',
    '/token',
    '/contributions',
    '/tasks',
    '/contracts',
    '/save',
    '/mint',
    '/market',
    '/exchange',
    '/react-on-chain-bugs'
  ];

  const shouldShowMainHeader = !pagesWithOwnHeaders.includes(location.pathname);

  // Toggle dark mode
  const toggleDarkMode = () => {
    setIsDarkMode(prev => {
      const newMode = !prev;
      localStorage.setItem('darkMode', newMode.toString());
      return newMode;
    });
  };

  // Apply dark mode class to body
  useEffect(() => {
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
  }, [isDarkMode]);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsSidebarOpen(window.innerWidth > 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Listen for exchange open event
  useEffect(() => {
    const handleOpenExchange = () => setShowExchange(true);
    window.addEventListener('openSpreadsheetExchange', handleOpenExchange);
    return () => window.removeEventListener('openSpreadsheetExchange', handleOpenExchange);
  }, []);

  // Load connected services from localStorage
  useEffect(() => {
    const loadConnectedServices = () => {
      const services = [];
      if (isAuthenticated && currentUser) {
        services.push('HandCash');
      }
      // Check for other connected services in localStorage
      const connected = localStorage.getItem('connectedServices');
      if (connected) {
        try {
          const parsed = JSON.parse(connected);
          services.push(...parsed);
        } catch (error) {
          console.error('Failed to parse connected services:', error);
        }
      }
      setConnectedServices(services);
    };
    
    loadConnectedServices();
    
    // Listen for service updates from connection modal
    const handleServicesUpdated = () => loadConnectedServices();
    window.addEventListener('servicesUpdated', handleServicesUpdated);
    
    return () => window.removeEventListener('servicesUpdated', handleServicesUpdated);
  }, [isAuthenticated, currentUser]);

  useEffect(() => {
    // Check if we're coming back from HandCash with an authToken
    const urlParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.replace('#', ''));
    
    // Check multiple possible parameter names HandCash might use
    const authToken = urlParams.get('authToken') || 
                     urlParams.get('auth_token') || 
                     urlParams.get('access_token') || 
                     urlParams.get('token') ||
                     hashParams.get('authToken') ||
                     hashParams.get('auth_token') ||
                     hashParams.get('access_token') ||
                     hashParams.get('token');
    
    console.log('App loading - URL:', window.location.href);
    console.log('URL params:', Array.from(urlParams.entries()));
    console.log('Hash params:', Array.from(hashParams.entries()));
    console.log('Auth token found:', authToken);
    
    if (authToken) {
      // We have an authToken, handle the callback
      console.log('Found authToken, handling callback...');
      const handcashService = new HandCashService();
      handcashService.handleCallback(authToken).then(user => {
        handleLogin(user);
        // Clean up URL after successful auth
        window.history.replaceState({}, document.title, window.location.pathname);
      }).catch(err => {
        console.error('Failed to handle callback:', err);
      });
    } else {
      checkAuthentication();
    }
  }, []);

  const checkAuthentication = () => {
    // Check if user is already logged in
    const cwiUser = getStoredCWIUser();
    if (cwiUser) {
      setCurrentUser(cwiUser);
      setIsAuthenticated(true);
      initializeBitcoinService();
    } else if (handcashService.isAuthenticated()) {
      const user = handcashService.getCurrentUser();
      setCurrentUser(user);
      setIsAuthenticated(true);
      initializeBitcoinService();
    } else {
      // Allow guest access - initialize Bitcoin service for anonymous users
      initializeBitcoinService();
    }
    setIsLoading(false);
  };

  const initializeBitcoinService = async () => {
    const service = new BitcoinService();
    await service.connect();
    setBitcoinService(service);
  };

  const handleLogin = (user: HandCashUser) => {
    setCurrentUser(user);
    setIsAuthenticated(true);
    initializeBitcoinService();
  };

  // Stable callback for the mobile app's silent bWallet sign-in.
  const handleMobileLogin = useCallback((user: HandCashUser) => {
    setCurrentUser(user);
    setIsAuthenticated(true);
  }, []);

  // Mobile / bWallet: the phone app owns the whole screen on "/".
  const mobileHome = hideShellChrome && location.pathname === '/';
  useEffect(() => {
    document.documentElement.classList.toggle('bw-mobile-app', mobileHome);
  }, [mobileHome]);

  // Prefer BRC-100 (window.CWI) inside bWallet; otherwise open the connections modal.
  const handleConnect = async () => {
    if (hasCWI()) {
      try {
        handleLogin(await signInWithCWI());
        return;
      } catch (err) {
        console.warn('bWallet sign-in failed, falling back', err);
      }
    }
    setShowConnectionsModal(true);
  };

  const handleLogout = () => {
    // Clear EVERYTHING
    localStorage.clear();
    sessionStorage.clear();
    
    // Clear all cookies
    document.cookie.split(";").forEach(function(c) { 
      document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/"); 
    });
    
    // Reset state
    setIsAuthenticated(false);
    setCurrentUser(null);
    setBitcoinService(null);
    
    // Force hard reload to clear all memory
    window.location.replace('/');
  };

  return (
    <>
      {!hideShellChrome && <ProofOfConceptBanner />}
      {!hideShellChrome && <DevSidebar />}
      <div className="app-with-sidebar">
      <Routes>
      <Route path="/bap" element={<BapsPage />} />
      <Route path="/developers" element={<BapsPage />} /> {/* Keep for backwards compatibility */}
      <Route path="/docs" element={<DocsPage />} />
      <Route path="/jobs" element={<JobExchangePage isAuthenticated={isAuthenticated} currentUser={currentUser} onLogout={handleLogout} />} />
      <Route path="/3d" element={<ThreeDPage />} />
      <Route path="/token" element={<TokenPage />} />
      <Route path="/contributions" element={<ContributionsPage />} />
      <Route path="/tasks" element={<TasksPage />} />
      <Route path="/contracts" element={<ContractsPage />} />
      <Route path="/educational-proposal" element={<EducationalProposalPage />} />
      <Route path="/bitcoin-spreadsheets" element={<BitcoinSpreadsheetsPage />} />
      <Route path="/marketing" element={<MarketingPage />} />
      <Route path="/save" element={<SavePage />} />
      <Route path="/mint" element={<MintPage />} />
      <Route path="/market" element={<MarketPage />} />
      <Route path="/exchange" element={<ExchangePage />} />
      <Route path="/react-on-chain-bugs" element={<ReactOnChainBugsPage />} />
      <Route path="/auth/handcash/callback" element={<HandCashCallback />} />
      <Route path="/" element={
        mobileHome ? (
          <MobileApp
            appName="bSheets"
            user={currentUser}
            onLogin={handleMobileLogin}
            onRequestLogin={() => new HandCashService().login()}
          />
        ) : isLoading ? (
          <div className="App">
            <div className="loading">Loading Bitcoin Jobs...</div>
          </div>
        ) : (
          <div className="App" style={{ paddingTop: isMobile ? '0' : hideShellChrome ? '28px' : '68px' }}> {/* 40px banner + 28px taskbar */}
            {/* Bitcoin Jobs Taskbar */}
            <SpreadsheetTaskbar
              isAuthenticated={isAuthenticated}
              currentUser={currentUser}
              onLogout={handleLogout}
              onNewSpreadsheet={() => {
                if (bitcoinService) {
                  bitcoinService.createSpreadsheet('New Spreadsheet').then(sheet => {
                    setCurrentSpreadsheet(sheet);
                  });
                }
              }}
              onSaveSpreadsheet={() => {
                // Trigger save functionality
                console.log('Save spreadsheet');
              }}
              onImport={() => {
                // Trigger import functionality
                console.log('Import spreadsheet');
              }}
              onExport={() => {
                // Trigger export functionality
                console.log('Export spreadsheet');
              }}
              toggleDarkMode={toggleDarkMode}
              isDarkMode={isDarkMode}
            />
            {shouldShowMainHeader && (
            <header className="App-header">
              {/* Mobile header layout - only on mobile */}
              <div className="mobile-header-wrapper">
                <div className="mobile-header-title">
                  <h1 
                    onClick={() => navigate('/')}
                    style={{ cursor: 'pointer' }}
                  >
                    <img 
                      src="/bitcoin-spreadsheets-icon.png" 
                      alt="" 
                      className="bitcoin-icon-beveled"
                      style={{ width: '40px', height: '40px', marginRight: '16px', verticalAlign: 'middle' }}
                    />
                    <span className="bitcoin-orange">Bitcoin</span> Spreadsheets
                  </h1>
                </div>
                <div className="mobile-header-connections">
                  <div className="connection-badge" onClick={handleConnect}>
                    {connectedServices.length === 0 ? (
                      <button className="connect-badge-btn">
                        {isInWallet() && hasCWI() ? 'Sign in' : 'Connect'}
                      </button>
                    ) : (
                      <>
                        <div className="connected-avatars">
                          {connectedServices.slice(0, 3).map((service, index) => (
                            <div 
                              key={service} 
                              className="service-avatar" 
                              style={{ 
                                marginLeft: index > 0 ? '-8px' : '0',
                                zIndex: 3 - index 
                              }}
                              title={service}
                            >
                              {service === 'HandCash' ? (
                                <img src="https://handcash.io/favicon.ico" alt="HandCash" />
                              ) : service === 'Salesforce' ? (
                                <div className="avatar-icon salesforce">S</div>
                              ) : service === 'Stripe' ? (
                                <div className="avatar-icon stripe">$</div>
                              ) : service === 'Google Sheets' ? (
                                <div className="avatar-icon sheets">G</div>
                              ) : service === 'QuickBooks' ? (
                                <div className="avatar-icon quickbooks">Q</div>
                              ) : (
                                <div className="avatar-icon default">{service.charAt(0)}</div>
                              )}
                            </div>
                          ))}
                          {connectedServices.length > 3 && (
                            <div 
                              className="service-avatar more-count" 
                              style={{ marginLeft: '-8px', zIndex: 0 }}
                            >
                              <span>+{connectedServices.length - 3}</span>
                            </div>
                          )}
                        </div>
                        <span className="connection-count">
                          {connectedServices.length} connected
                        </span>
                      </>
                    )}
                  </div>
                  {/* Mobile user section */}
                  {isAuthenticated && currentUser && (
                    <div className="mobile-user-section">
                      <div className="handcash-user-badge">
                        <img 
                          src="https://handcash.io/favicon.ico" 
                          alt="HandCash" 
                          className="handcash-user-icon"
                        />
                        <span className="user-handle">@{currentUser.handle}</span>
                        <div className="connection-dot"></div>
                      </div>
                      <button className="mobile-logout-btn" onClick={handleLogout}>
                        Exit
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Desktop header layout - hidden on mobile */}
              <div className="desktop-header-wrapper">
                <h1
                  onClick={() => navigate('/')}
                  style={{ cursor: 'pointer' }}
                >
                  <img 
                    src="/bitcoin-spreadsheets-icon.png" 
                    alt="" 
                    className="bitcoin-icon-beveled"
                    style={{ width: '40px', height: '40px', marginRight: '16px', verticalAlign: 'middle' }}
                  />
                  <span className="bitcoin-orange">Bitcoin</span> Spreadsheets
                </h1>
                <p className="app-subtitle">Secure, encrypted spreadsheets on the blockchain</p>
              </div>
              
              {/* Desktop user info (top right) */}
              <div className="user-info desktop-user-info">
                {isAuthenticated && currentUser ? (
                  <>
                    <div className="handcash-user-badge">
                      <img 
                        src="https://handcash.io/favicon.ico" 
                        alt="HandCash" 
                        className="handcash-user-icon"
                      />
                      <span className="user-handle">@{currentUser.handle}</span>
                      <div className="connection-dot"></div>
                    </div>
                    <button 
                      className="connections-button"
                      onClick={() => setShowConnectionsModal(true)}
                      style={{
                        padding: '10px 24px',
                        backgroundColor: '#4285F4',
                        color: 'white',
                        border: 'none',
                        borderRadius: '12px',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        transition: 'all 0.2s ease',
                        marginRight: '8px',
                        minWidth: '160px',
                        justifyContent: 'center',
                        boxShadow: '0 2px 8px rgba(66, 133, 244, 0.2)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#3367D6';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(66, 133, 244, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#4285F4';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(66, 133, 244, 0.2)';
                      }}
                    >
                      <span style={{ fontSize: '16px' }}>⚙️</span>
                      Manage Connections
                    </button>
                    <button className="logout-btn" onClick={handleLogout}>
                      Sign Out
                    </button>
                  </>
                ) : (
                  <>
                    <button 
                      className="connect-button"
                      onClick={() => setShowConnectionsModal(true)}
                      style={{
                        padding: '10px 24px',
                        backgroundColor: '#4285F4',
                        color: 'white',
                        border: 'none',
                        borderRadius: '12px',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        transition: 'all 0.2s ease',
                        minWidth: '140px',
                        justifyContent: 'center',
                        boxShadow: '0 2px 8px rgba(66, 133, 244, 0.2)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#3367D6';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(66, 133, 244, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#4285F4';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(66, 133, 244, 0.2)';
                      }}
                    >
                      <span style={{ fontSize: '16px' }}>🔗</span>
                      Connect
                    </button>
                  </>
                )}
              </div>

              {/* Mobile user info (below title) */}
              <div className="mobile-user-info">
                {isAuthenticated && currentUser ? (
                  <div className="mobile-auth-section">
                    <div className="handcash-user-badge">
                      <img 
                        src="https://handcash.io/favicon.ico" 
                        alt="HandCash" 
                        className="handcash-user-icon"
                      />
                      <span className="user-handle">@{currentUser.handle}</span>
                      <div className="connection-dot"></div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <button 
                        className="connections-button mobile-connections-btn"
                        onClick={() => setShowConnectionsModal(true)}
                        style={{
                          padding: '10px 20px',
                          backgroundColor: '#4285F4',
                          color: 'white',
                          border: 'none',
                          borderRadius: '12px',
                          fontSize: '14px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          boxShadow: '0 2px 8px rgba(66, 133, 244, 0.2)'
                        }}
                      >
                        Manage Connections
                      </button>
                      <button className="logout-btn mobile-logout-btn" onClick={handleLogout}>
                        Sign Out
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="mobile-login-section">
                    <button 
                      className="connect-button mobile-connect"
                      onClick={() => setShowConnectionsModal(true)}
                      style={{
                        padding: '12px 24px',
                        backgroundColor: '#4285F4',
                        color: 'white',
                        border: 'none',
                        borderRadius: '12px',
                        fontSize: '15px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        width: '100%',
                        maxWidth: '200px',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 2px 8px rgba(66, 133, 244, 0.2)'
                      }}
                    >
                      Connect
                    </button>
                  </div>
                )}
              </div>
            </header>
            )}
            <main className="main-container">
              {bitcoinService ? (
                showExchange ? (
                  <SpreadsheetExchangeView 
                    onClose={() => setShowExchange(false)}
                    onSelectSpreadsheet={(spreadsheet) => {
                      console.log('Selected spreadsheet from exchange:', spreadsheet);
                      // Could load the spreadsheet data here
                      setShowExchange(false);
                    }}
                  />
                ) : (
                  <div className="app-content">
                    {isSidebarOpen && (
                      <SpreadsheetManager
                        bitcoinService={bitcoinService}
                        onSelectSpreadsheet={setCurrentSpreadsheet}
                        currentSpreadsheet={currentSpreadsheet}
                      />
                    )}
                    <div className="spreadsheet-wrapper">
                      {isMobile ? (
                        <MobileSpreadsheet
                          spreadsheetData={currentSpreadsheet}
                          onSpreadsheetUpdate={setCurrentSpreadsheet}
                          isDarkMode={isDarkMode}
                        />
                      ) : (
                        <Spreadsheet 
                          bitcoinService={bitcoinService}
                          spreadsheet={currentSpreadsheet}
                          onSpreadsheetUpdate={setCurrentSpreadsheet}
                          isAuthenticated={isAuthenticated}
                          isSidebarOpen={isSidebarOpen}
                          onToggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
                          onLogin={() => {
                            const loginService = new HandCashService();
                            loginService.login();
                          }}
                          onNewSpreadsheet={() => {
                            if (bitcoinService) {
                              bitcoinService.createSpreadsheet('New Spreadsheet').then(sheet => {
                                setCurrentSpreadsheet(sheet);
                              });
                            }
                          }}
                        />
                      )}
                    </div>
                  </div>
                )
              ) : (
                <div className="loading">Initializing blockchain connection...</div>
              )}
            </main>
            
            {/* Claude AI Chat Button */}
            <button 
              className="claude-chat-button"
              onClick={() => setIsClaudeChatOpen(true)}
              style={{
                position: 'fixed',
                bottom: '20px',
                right: '20px',
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #87CEEB, #4682B4)',
                border: 'none',
                color: 'white',
                fontSize: '24px',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(135, 206, 235, 0.3)',
                display: isClaudeChatOpen ? 'none' : 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease',
                zIndex: 999
              }}
              title="Chat with Claude AI"
            >
              💬
            </button>
            
             {/* Claude AI Chat Component */}
             <ClaudeChat 
               spreadsheetData={currentSpreadsheet?.cells ? Object.values(currentSpreadsheet.cells) : []}
               isOpen={isClaudeChatOpen}
               onClose={() => setIsClaudeChatOpen(false)}
             />
             
             {/* Enhanced Connections Modal */}
             <EnhancedConnectionsModal
               isOpen={showConnectionsModal}
               onClose={() => setShowConnectionsModal(false)}
               onLogin={handleLogin}
               bitcoinService={bitcoinService}
               isDarkMode={isDarkMode}
               onDataImport={(data) => {
                 // Handle imported spreadsheet data
                 setCurrentSpreadsheet(data);
                 console.log('Imported spreadsheet:', data);
               }}
             />
             
             {/* PWA Install Prompt */}
             <InstallPrompt />
             
          </div>
        )
      } />
      <Route path="*" element={
        <div className="App">
          <header className="App-header">
            <h1>404 - Page Not Found</h1>
            <p>The page you're looking for doesn't exist.</p>
            <button onClick={() => navigate('/')} style={{
              padding: '10px 20px',
              margin: '20px',
              backgroundColor: '#f7931a',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}>
              Go Home
            </button>
          </header>
        </div>
      } />
    </Routes>
      {!hideShellChrome && <Footer />}
      
      {/* Bitcoin OS Dock - Global on all pages */}
      {!hideShellChrome && <DockManager currentApp="bitcoin-spreadsheets" />}
      </div>
    </>
  );
}

export default App;